<template>
  <v-container
    fluid
    class="cup-container p-0"
    style="max-width: 100%; margin: 0"
  >
    <!-- 우승 화면 (winner 존재 시) -->
    <div
      v-if="winner"
      class="full-area d-flex flex-column align-center justify-center text-center"
    >
      <v-row justify="center" class="mb-3">
        <v-col cols="12" md="4" class="d-flex flex-column justify-start">
          <v-card outlined class="candidate-card flex-fill text-center">
            <v-card-title class="card-title-normal">
              우승: {{ winner.name }}
            </v-card-title>
            <v-img :src="winner.img" class="candidate-image" />
            <v-card-text class="card-text-normal">
              {{ winner.desc }}
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- 자동 전송 후 메시지 표시 -->
      <div v-if="resultMsg" class="mt-2">
        {{ resultMsg }}
      </div>
    </div>

    <!-- 진행 중 화면 -->
    <div v-else class="full-area d-flex flex-column align-center justify-start">
      <!-- 이번 라운드 매치가 남아있을 때 -->
      <div
        v-if="currentMatch"
        class="full-area"
        style="width: 100%; height: 100%"
      >
        <v-row
          wrap
          align="stretch"
          justify="center"
          class="match-row"
          style="margin: 0"
        >
          <!-- 왼쪽 카드 -->
          <v-col cols="12" md="4" class="d-flex flex-column justify-start">
            <v-card
              outlined
              class="candidate-card flex-fill"
              @click="selectWinner(currentMatch[0])"
            >
              <v-card-title class="card-title-normal">
                {{ currentMatch[0].name }}
              </v-card-title>
              <v-img :src="currentMatch[0].img" class="candidate-image" />
              <v-card-text class="card-text-normal">
                {{ currentMatch[0].desc }}
              </v-card-text>
            </v-card>
          </v-col>

          <!-- 중앙 VS -->
          <v-col cols="12" md="2" class="d-flex justify-center align-center">
            <span class="vs-text">VS</span>
          </v-col>

          <!-- 오른쪽 카드 -->
          <v-col cols="12" md="4" class="d-flex flex-column justify-start">
            <v-card
              outlined
              class="candidate-card flex-fill"
              @click="selectWinner(currentMatch[1])"
            >
              <v-card-title class="card-title-normal">
                {{ currentMatch[1].name }}
              </v-card-title>
              <v-img :src="currentMatch[1].img" class="candidate-image" />
              <v-card-text class="card-text-normal">
                {{ currentMatch[1].desc }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </v-container>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import axios from "axios";
// 32강용 데이터
import { engineerData } from "@/data/engineerCupData.js";

export default {
  name: "EngineerCup",
  emits: ["update-round-title"],
  setup(props, { emit }) {
    const route = useRoute();

    // URL query로 사용자 정보 받기 (예시)
    const userArtech = route.query.artech_collab_count || "";
    const userGender = route.query.gender || "";
    const userAgeRange = route.query.ageRange || "";
    const userJob = route.query.job || "";
    const userCountry = route.query.country || "";

    // 라운드/후보 상태
    const round = ref(1); // 1=32강, 2=16강, 3=8강, 4=4강, 5=결승
    const currentRoundCandidates = ref([...engineerData]);
    const nextRoundCandidates = ref([]);
    const currentMatchIndex = ref(0);
    const winner = ref(null);

    // 결과 메시지 & 대결 결과 기록
    const resultMsg = ref("");
    const matchResults = ref([]);

    // 라운드명: 32강 / 16강 / 8강 / 4강 / 결승
    const currentRoundTitle = computed(() => {
      switch (round.value) {
        case 1:
          return "32강";
        case 2:
          return "16강";
        case 3:
          return "8강";
        case 4:
          return "4강";
        case 5:
          return "결승";
        default:
          return "결승";
      }
    });

    // 현재 매치 (2명)
    const currentMatch = computed(() => {
      const idx = currentMatchIndex.value * 2;
      if (idx + 1 < currentRoundCandidates.value.length) {
        return [
          currentRoundCandidates.value[idx],
          currentRoundCandidates.value[idx + 1],
        ];
      }
      return null;
    });

    // "32강(1/16)" 같은 표시
    const roundTitleWithProgress = computed(() => {
      const total = currentRoundCandidates.value.length / 2;
      if (currentMatch.value) {
        return `${currentRoundTitle.value}(${
          currentMatchIndex.value + 1
        }/${total})`;
      } else {
        return `${currentRoundTitle.value}`;
      }
    });

    // 부모(App.vue)에 라운드 정보 전달
    const sendRoundTitleToParent = () => {
      emit("update-round-title", roundTitleWithProgress.value);
    };

    // 컴포넌트 마운트 시
    onMounted(() => {
      sendRoundTitleToParent();
    });

    // 컴포넌트 언마운트 시 → 부모의 라운드 정보 초기화
    onUnmounted(() => {
      emit("update-round-title", "");
    });

    // 매치에서 승자 선택
    const selectWinner = (candidate) => {
      const idx = currentMatchIndex.value * 2;
      const cA = currentRoundCandidates.value[idx];
      const cB = currentRoundCandidates.value[idx + 1];

      // 기록 저장
      matchResults.value.push({
        round: round.value,
        matchIndex: currentMatchIndex.value + 1,
        candidateA: cA.name,
        candidateB: cB.name,
        chosen: candidate.name,
      });

      nextRoundCandidates.value[currentMatchIndex.value] = candidate;
      currentMatchIndex.value++;

      // 모든 매치를 마쳤다면(currentMatch가 null) 다음 라운드
      if (!currentMatch.value) {
        goNextRound();
      } else {
        sendRoundTitleToParent();
      }
    };

    // 라운드가 끝났으면 다음 라운드로
    const goNextRound = () => {
      round.value++;
      currentRoundCandidates.value = [...nextRoundCandidates.value];
      nextRoundCandidates.value = [];
      currentMatchIndex.value = 0;

      // 5라운드(결승)까지 끝났으면(=round.value가 6이 됐으면) 우승자 결정
      if (round.value > 5) {
        winner.value = currentRoundCandidates.value[0];
        // 우승자가 결정되는 순간 자동 전송
        submitAllData();
      } else {
        sendRoundTitleToParent();
      }
    };

    // 최종 결과 전송
    const GAS_URL =
      "https://script.google.com/macros/s/AKfycbwZddH4phffDw-C_3p2rM-F94pzK8EMYVe5Sm1w8MCZ0PdfqJVTSMTzgMEjj6TLz__O1A/exec";

    const submitAllData = async () => {
      try {
        if (!winner.value) {
          resultMsg.value = "아직 우승자가 없습니다.";
          return;
        }
        const matchResultsStr = JSON.stringify(matchResults.value);

        // IP 가져오기
        const ipRes = await axios.get("https://api.ipify.org?format=json");
        const ip = ipRes.data.ip;

        const body = new URLSearchParams({
          mode: "submitAll",
          cup_id: "EngineerCup",
          // 사용자 정보
          artech_collab_count: userArtech,
          gender: userGender,
          age_range: userAgeRange,
          job: userJob,
          country: userCountry,
          ip,
          // 최종 결과
          finalWinner: winner.value.name,
          matchResults: matchResultsStr,
        });

        const resp = await axios.post(GAS_URL, body, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
        if (resp.data.status === "ok") {
          resultMsg.value = "";
        } else {
          resultMsg.value = resp.data.message || "";
        }
      } catch (err) {
        resultMsg.value = err.message;
      }
    };

    return {
      // Reactive
      round,
      currentRoundCandidates,
      nextRoundCandidates,
      currentMatchIndex,
      winner,
      resultMsg,
      matchResults,

      // Computed
      currentRoundTitle,
      currentMatch,
      roundTitleWithProgress,

      // Methods
      selectWinner,
      goNextRound,
      submitAllData,
    };
  },
};
</script>

<style scoped>
/* 페이지 컨테이너 */
.cup-container {
  min-height: calc(100vh - 64px);
  padding-top: 16px;
}

/* 전체 영역 */
.full-area {
  width: 100%;
  min-height: calc(100vh - 64px - 60px);
}

/* 매치 영역 */
.match-row {
  width: 100%;
  margin: 0;
  padding: 0;
}

/* 카드 레이아웃 */
.candidate-card {
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

/* 카드 이미지 */
.candidate-image {
  width: 100%;
  height: auto;
  object-fit: contain;
}

/* VS 텍스트 */
.vs-text {
  font-size: 24px;
  font-weight: bold;
}

/* 카드 제목 - 여러 줄 가능, '...'로 생략되지 않도록 */
.card-title-normal {
  font-size: 25px;
  line-height: 1.4;
  font-weight: bold;
  white-space: pre-wrap; /* \n 처리 & 자동 줄바꿈 */
  overflow: visible; /* 길어도 잘리지 않음 */
  text-overflow: unset; /* '...' 비활성화 */
  word-wrap: break-word;
  word-break: break-all;
}

/* 카드 텍스트 - 여러 줄 가능, '...'로 생략되지 않도록 */
.card-text-normal {
  font-size: 17px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow: visible;
  text-overflow: unset;
  word-wrap: break-word;
  word-break: break-all;
}

/* 모바일 대응 */
@media (max-width: 768px) {
  .full-area,
  .match-row {
    height: auto !important;
  }
}
</style>
